import React from 'react';
import { nanoid } from 'nanoid';
import { StaticImage } from 'gatsby-plugin-image';
import {
  Text,
  Flex,
  Box,
  Table,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Tooltip,
  Button,
} from '@chakra-ui/react';
import { Link } from '@hover/blueprint';

const HEADERS = [
  {},
  {
    name: 'Pay As You Go',
    description: 'Pay full price per property',
    ctaButton: {
      label: 'First Job Free',
      href: './signup/',
    },
  },
  {
    name: 'Measure',
    description: '$2500/yr. + discounted per property pricing',
    ctaButton: {
      label: 'Get Started',
      href: './signup/',
    },
  },
  {
    name: 'Scale',
    description: '$5000/yr. + discounted per property pricing',
    ctaButton: {
      label: 'Contact Sales',
      href: 'https://calendly.com/hover-sdr/hover-membership-website',
    },
  },
  {
    name: 'Transform',
    description: 'Custom + discounted per property pricing',
    buttonText: 'Contact Sales',
    ctaButton: {
      label: 'Contact Sales',
      href: 'https://calendly.com/hover-sdr/hover-membership-website',
    },
  },
];

const GreenCheckImage = (
  <StaticImage
    src="../../assets/images/icon-green-check.png"
    alt="green check"
    style={{ width: 20 }}
  />
);

const notIncludedImage = (
  <StaticImage
    src="../../assets/images/icon-red-x.png"
    alt="not included"
    style={{ width: 16 }}
  />
);

const SECTIONS = [
  {
    sectionName: 'Foundations',
    features: [
      {
        feature: 'Photo Capture',
        tooltip:
          'Store all your properties, measurements, and models in our secure cloud infrastructure',
        values: [
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Property Storage',
        tooltip:
          'Industry-leading accuracy is presented in a familiar PDF deliverable',
        values: ['12 months', 'Unlimited', 'Unlimited', 'Unlimited'],
      },
      {
        feature: 'Measurement PDF',
        tooltip:
          'Industry-leading accuracy is presented in a familiar PDF deliverable',
        values: [
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Interactive 3D Model',
        tooltip: 'View real products from real manufacturers on the 3D model',
        values: [
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Multi-platform Access',
        tooltip:
          'Access your measurements, 3D models, photos and notes from our iOS, Android, and web apps.',
        values: [
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Beacon 3D+ Prospect', // should this be renamed?
        tooltip:
          'Capture and store unlimited property photos at no cost, and order the measurements and 3D model when you are ready',
        values: [
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Api Access',
        tooltip:
          'Programmatic access to our ecosystem to build robust integrations with other software systems in order to streamline workflows and save time',
        values: [
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Data Exports',
        tooltip:
          'Export measurement and 3D model data from Beacon 3D+ in file formats usable by other software in your existing workflow',
        values: [
          'PDF, XLS',
          'PDF, XLS, XML, JSON',
          'PDF, XLS, XML, JSON, SKP, DWG, DXF, .esx',
          'PDF, XLS, XML, JSON, SKP, DWG, DXF, .esx',
        ],
      },
      {
        feature: 'Expedited Job Processing',
        tooltip:
          'Jump higher in the queue to get time-sensitive jobs returned more quickly (additional fee)',
        values: ['+$40', '+$30', '+$20', '+$20'],
      },
      {
        feature: 'Property Discounts',
        tooltip:
          'Beacon 3D+ pricing is a combination of a SaaS-based subscription plus per-job transactional charges. If on a subscription plan, users are granted discounts on every property they submit.',
        values: [
          'None',
          '$10 off all jobs',
          '$15+ off all jobs',
          '$15+ off all jobs',
        ],
      },
      {
        feature: 'Unlimited Users',
        tooltip:
          'Measure, Scale, and Transform plans allow for unlimited users with a single subscription price.',
        values: [
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
    ],
  },

  {
    sectionName: 'Workflow Enhancements',
    features: [
      {
        feature: 'Beacon 3D+ Connect',
        tooltip:
          'Invite anyone to photograph a property for you using Beacon 3D+ Connect, for which you will receive full measurements and a 3D model',
        values: [
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Measurement Report Branding',
        tooltip:
          'Add your logo to all measurement PDFs</span></div>Measurement Report Branding',
        values: [
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Gallery and Screenshot Mode',
        tooltip:
          'Add real products from real manufacturers to 3D models of any property, create multiple versions, and showcase these options to clients',
        values: [
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Integrations (Home Improvement Professionals)',
        tooltip:
          'Eliminate manual data entry by integrating Beacon 3D+ into your existing CRM, Job management, and financing software - JobNimbus, JobProgress, Leap to Digital, CompanyCam, Zapier, i360, Restoration AI, SalesRabbit, Hatch, GAF Project, Mosaic',
        values: [
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Integrations (Insurance)',
        tooltip:
          'Eliminate manual data entry by integrating Beacon 3D+ into your existing claims management and estimation software - CoreLogic (Symbility), OnSeen, Accurence, FileTrac, ClaimAssist, VCA',
        values: [
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
    ],
  },

  {
    sectionName: 'Enterprise Features',
    features: [
      {
        feature: 'Priority Support',
        tooltip: 'Jump to the front of the line with priority support queues',
        values: [
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Priority Job Processing',
        tooltip:
          'Unlock quicker average delivery without paying for expedited job processing',
        values: [
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Assigned Account Manager',
        tooltip:
          'Talk to an assigned Account Manager for questions about the product or your business',
        values: [
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Single Sign-on (SSO)',
        tooltip:
          'Enable enterprise-grade security and authentication for your entire team',
        values: [
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Advanced Admin and User Permissions',
        tooltip:
          'Control access to certain features for third parties and internal team members',
        values: [
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
          GreenCheckImage,
        ],
      },
    ],
  },

  {
    sectionName: 'End-to-end Estimation and Order Management',
    features: [
      {
        feature: 'Estimation',
        tooltip:
          'Accurate estimates for multiple trades in seconds that reflect job complexity and your unique pricing',
        values: [
          notIncludedImage,
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Proposals and Contracts',
        tooltip:
          'Automatically populate your contract templates and get digital signatures while onsite',
        values: [
          notIncludedImage,
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Production Console',
        tooltip:
          'Demystify and speed up project management with all the dependable resources you need to produce a job; from detailed takeoffs to measurements and job economic',
        values: [
          notIncludedImage,
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Supplier-Direct Ordering',
        tooltip:
          'Order electronically form leading suppliers with zero manual entry and without leaving the console',
        values: [
          notIncludedImage,
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Materials Management',
        tooltip: 'Precise, pre-populated, material lists for multiple trades',
        values: [
          notIncludedImage,
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Labor Management',
        tooltip:
          'Generate work orders in a click that reflect a project’s specific installation needs',
        values: [
          notIncludedImage,
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Onboarding',
        tooltip:
          'White glove service to customize Beacon 3D+ features to reflect your pricing and process in weeks, not months',
        values: [
          notIncludedImage,
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
        ],
      },
      {
        feature: 'Training and Certification',
        tooltip:
          'Get started off on the right foot with customized training and education',
        values: [
          notIncludedImage,
          notIncludedImage,
          notIncludedImage,
          GreenCheckImage,
        ],
      },
    ],
  },
];

const featureComparisonTableHeader = (
  <Thead>
    <Tr>
      {HEADERS.map(({ name, description, ctaButton }) =>
        name ? (
          <Th textTransform="none" key={name} fontSize={['xs', 'sm', 'md']}>
            <Flex direction="column" justifyContent="space-between" minH="36">
              <Box textAlign="center">
                <Text fontWeight="bold" mb="3">
                  {name}
                </Text>
                <Text mb="3" fontWeight="normal">
                  {description}
                </Text>
              </Box>
              <Button
                as={Link}
                href={ctaButton.href}
                size="sm"
                variant="outline"
                fontSize={['xx-small', 'sm', 'md']}
              >
                {ctaButton.label}
              </Button>
            </Flex>
          </Th>
        ) : (
          <Th key="blank" />
        ),
      )}
    </Tr>
  </Thead>
);

const featureComparisonTableBody = SECTIONS.map(({ sectionName, features }) => (
  <Tbody w="100%" key={sectionName}>
    <Tr>
      <Td>
        <Text fontSize="lg" mt="9" mb="5" fontWeight="bold">
          {sectionName}
        </Text>
      </Td>
    </Tr>

    {features.map(({ feature, tooltip, values }, rowIndex) => {
      const backgroundColor =
        rowIndex % 2 === 0 ? 'rgb(249, 252, 255)' : 'white';

      return (
        <Tr
          height="24"
          backgroundColor={backgroundColor}
          color="#333"
          key={feature}
        >
          <Td>
            <Tooltip
              label={tooltip}
              placement="top-start"
              background="hoverBlue"
              borderRadius="5"
              padding="2"
              closeOnClick={false}
            >
              <Text
                as="span"
                borderBottomWidth="medium"
                borderBottomStyle="dashed"
              >
                {feature}
              </Text>
            </Tooltip>
          </Td>
          {values.map((value: React.ReactNode | string) => (
            <Td key={nanoid()} maxW="36" textAlign="center">
              {value}
            </Td>
          ))}
        </Tr>
      );
    })}
  </Tbody>
));

const FeatureComparisonTable = () => (
  <Box px="5">
    <Box overflowX="scroll" mx="auto" maxW="5xl">
      <Table variant="unstyled" size="md">
        {featureComparisonTableHeader}
        {featureComparisonTableBody}
      </Table>
    </Box>
  </Box>
);

export default FeatureComparisonTable;
